import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Reusable "Coming soon" scaffold for format pages that don't have
 * content yet (Watch / Listen / Use). Honest, minimal, and points
 * readers to what's already live and to the newsletter.
 */
export default function ComingSoonFormat({
  label,
  headline,
  description,
  bullets = [],
  Icon,
  testId,
}) {
  return (
    <div data-testid={testId}>
      <section className="border-b border-oc-hairline bg-oc-sand">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-9">
              <div className="eyebrow text-oc-coral mb-6 flex items-center gap-3">
                {Icon && <Icon className="w-4 h-4" />} {label}
                <span className="ml-3 font-ui text-xs font-bold uppercase tracking-widest text-oc-ink-muted border border-oc-hairline px-2 py-0.5">
                  Coming soon
                </span>
              </div>
              <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink max-w-4xl">
                {headline}
              </h1>
              <p className="mt-8 font-editorial text-xl md:text-2xl text-oc-ink-soft leading-snug max-w-3xl">
                {description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            {bullets.length > 0 && (
              <>
                <div className="eyebrow text-oc-coral mb-6">What to expect</div>
                <ul className="space-y-5">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex gap-4 font-editorial text-lg text-oc-ink leading-snug">
                      <span className="font-ui font-bold text-oc-coral text-xs mt-1.5 w-7 flex-shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/read"
                className="inline-flex items-center h-12 px-6 bg-oc-ink text-white font-ui text-xs font-bold uppercase tracking-widest hover:bg-oc-blue transition-colors"
              >
                Read what&#39;s live now <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Link>
              <Link
                to="/newsletter"
                className="inline-flex items-center h-12 px-6 border border-oc-ink font-ui text-xs font-bold uppercase tracking-widest hover:bg-oc-ink hover:text-white transition-colors"
              >
                Get notified when it launches
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="border-l-2 border-oc-blue pl-6 py-2">
              <div className="eyebrow text-oc-ink-muted mb-3">Want to help shape this?</div>
              <p className="font-editorial text-lg text-oc-ink leading-snug">
                We&#39;re building this format around what operators actually need. If you have suggestions, requests, or want to contribute early, get in touch.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral"
              >
                Send us a note <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
